import { z } from 'zod'
import axios from 'axios'
import { useMutation, useQuery, useSuspenseQuery } from '@tanstack/react-query'
import {
  CommentsSchema,
  ReactionsSchema,
  ReactionsTypeSchema,
} from '../socials/api'
import { useCompetitionId } from '../common/hooks'

export type ApiArenaPostsFilterType = {
  page: number
  page_size: number
  competition_id: number
  search?: string
  author?: number
  created_before?: string
  created_after?: string
}

async function getArenaPosts(params: ApiArenaPostsFilterType) {
  const response = await axios.get(
    `/api/competitions/${params.competition_id}/posts`,
    {
      params,
    }
  )
  return ArenaPostsResponseSchema.parse(response.data)
}

export function useArenaPosts(params: ApiArenaPostsFilterType) {
  return useSuspenseQuery({
    queryKey: ['arenaPosts', params],
    queryFn: () => getArenaPosts(params),
  })
}

async function createArenaPost(
  createArenaPost: CreateArenaPost & {
    image_id?: number | null
    video_id?: number | null
  }
) {
  const { image_id, video_id, ...postData } = createArenaPost
  const validatedData = CreateArenaPostSchema.parse(postData)
  const response = await axios.post(
    `/api/competitions/${validatedData.competition_id}/posts`,
    {
      text: validatedData.text,
      is_roar: validatedData.is_roar,
      image_id: image_id,
      video_id: video_id,
    }
  )
  return response.data
}

export function useCreateArenaPost() {
  return useMutation({
    mutationFn: createArenaPost,
  })
}

async function deleteArenaPost({
  competition_id,
  post_id,
}: {
  competition_id: number
  post_id: number
}) {
  const response = await axios.delete(
    `/api/competitions/${competition_id}/posts/${post_id}`
  )
  return response.data
}

export function useDeleteArenaPost() {
  return useMutation({
    mutationFn: deleteArenaPost,
  })
}

async function getArenaPost({
  competition_id,
  post_id,
}: {
  competition_id: number
  post_id: number
}) {
  const response = await axios.get(
    `/api/competitions/${competition_id}/posts/${post_id}`
  )
  return SingleArenaPostSchema.parse(response.data)
}

export function useArenaPost({
  competition_id,
  post_id,
  should_load,
}: {
  competition_id: number
  post_id: number
  should_load: boolean
}) {
  return useQuery({
    queryKey: ['arenaPost', { competition_id, post_id }],
    queryFn: () => getArenaPost({ competition_id, post_id }),
    enabled: should_load,
  })
}

async function updateArenaPost({
  competition_id,
  is_roar,
  text,
  image_id,
  video_id,
  post_id,
}: {
  competition_id: number
  is_roar: boolean
  text: string | null
  image_id?: number | null
  video_id?: number | null
  post_id: number
}) {
  const response = await axios.put(
    `/api/competitions/${competition_id}/posts/${post_id}`,
    {
      image_id: image_id,
      video_id: video_id,
      is_roar: is_roar,
      text: text,
    }
  )
  return response.data
}

export function useEditArenaPost() {
  return useMutation({
    mutationFn: updateArenaPost,
  })
}

export const BaseArenaPostSchema = z.object({
  post_id: z.number(),
  updated_at: z.string(),
  text: z.string().nullable(),
  user_id: z.number(),
  user_first_name: z.string(),
  user_last_name: z.string(),
  user_photo: z.string().nullable(),
  player_id: z.number().nullable(),
  competition_id: z.number(),
  is_roar: z.boolean(),
  created_at: z.string(),
  image: z.object({ main: z.string() }).partial(),
  video: z.object({ main: z.string() }).partial(),
})

export const ArenaPostsSchema = BaseArenaPostSchema.extend({
  comment_count: z.number(),
  reactions: ReactionsTypeSchema.partial().default({}),
})

export const SingleArenaPostSchema = BaseArenaPostSchema.extend({
  comments: z.array(CommentsSchema),
  reactions: z.array(ReactionsSchema),
})

export const ArenaPostsResponseSchema = z.object({
  total: z.number(),
  posts: z.array(ArenaPostsSchema),
})

const CreateArenaPostSchema = BaseArenaPostSchema.pick({
  competition_id: true,
  is_roar: true,
  text: true,
})

type CreateArenaPost = z.infer<typeof CreateArenaPostSchema>
export type ArenaPost = z.infer<typeof ArenaPostsSchema>

// Top Arena Users
export const TopArenaUserSchema = z.object({
  user_id: z.number(),
  name: z.string(),
  photo: z.string().nullable(),
  post_count: z.number(),
})

export type TopArenaUser = z.infer<typeof TopArenaUserSchema>

async function getTopArenaUsers({
  count,
  competition_id,
}: {
  count: number
  competition_id: number
}) {
  const response = await axios.get('/api/arena/top_users', {
    params: {
      count,
      competition_id,
    },
  })
  return z.array(TopArenaUserSchema).parse(response.data)
}

export function useTopArenaPosters(count: number = 10) {
  const competition_id = useCompetitionId()

  return useSuspenseQuery({
    queryKey: ['topArenaPosters', { count, competition_id }],
    queryFn: () => getTopArenaUsers({ count, competition_id }),
  })
}

async function getArenaPostAuthors({
  competition_id,
}: {
  competition_id: number
}) {
  const { data } = await axios.get(
    `/api/competitions/${competition_id}/posts/authors`
  )
  return z.array(ArenaPostAuthorSchema).parse(data)
}

export function useArenaPostAuthors({
  competition_id,
}: {
  competition_id: number
}) {
  return useSuspenseQuery({
    queryKey: ['arenaPostAuthors', competition_id],
    queryFn: () => getArenaPostAuthors({ competition_id }),
  })
}

export const ArenaPostAuthorSchema = z.object({
  user_id: z.number(),
  name: z.string(),
})

export type ArenaPostAuthor = z.infer<typeof ArenaPostAuthorSchema>
